// "New template" (template-page spec §4): the same cards before the
// template exists — every field works off form state — with Create / Cancel.
// No Used by or Danger zone: there is nothing to use or delete yet.

import { SectionNav, toast } from "@pej/kore/components";
import { Link, useNavigate } from "@tanstack/react-router";
import { useRef } from "react";
import { useCreateTemplate } from "@/api/templates";
import { PageSaveBar } from "@/components/section-page/page-save-bar";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { useUnsavedChangesGuard } from "@/components/section-page/use-unsaved-changes-guard";
import { TemplateFormCards } from "./template-form-cards";
import { NEW_TEMPLATE_DEFAULTS } from "./template-schema";
import { TemplatePreview, templateNavItems } from "./template-sections";
import { useTemplateForm } from "./use-template-form";

export function CreateTemplatePage({ workspaceId }: { workspaceId: string }) {
    const navigate = useNavigate();
    const createTemplate = useCreateTemplate(workspaceId);
    // A successful create leaves on purpose: the guard lets it through. Cancel
    // goes through the guard, so it asks only when something was edited
    // (spec §4). Set just before navigating, with no render in between.
    const createdRef = useRef(false);
    const editor = useTemplateForm({
        defaultValues: NEW_TEMPLATE_DEFAULTS,
        persist: (body) => createTemplate.mutateAsync(body),
        onSaved: (created) => {
            createdRef.current = true;
            toast.success("Template created");
            navigate({ to: "/w/$workspaceId/templates/$templateId", params: { workspaceId, templateId: created.id } });
        },
        failureTitle: "Create failed",
        armed: true,
    });
    const dirtyRef = useRef(false);
    dirtyRef.current = editor.isDirty;
    const { dialog } = useUnsavedChangesGuard(() => !createdRef.current && dirtyRef.current);

    return (
        <SectionPageShell
            backLabel="Templates"
            backLink={<Link to="/w/$workspaceId/templates" params={{ workspaceId }} />}
            title="New template"
            nav={<SectionNav items={templateNavItems(false)} sticky={false} />}
            preview={<TemplatePreview values={editor.values} />}
            previewFirst
        >
            <TemplateFormCards editor={editor} />
            {/* Armed from the first paint: defaults that are all acceptable
                are still worth creating. The guard above still keys on real
                edits, so Cancel on an untouched page leaves without asking. */}
            <PageSaveBar
                dirty
                saving={createTemplate.isPending}
                saveLabel="Create template"
                cancelLabel="Cancel"
                status="New template · not yet saved"
                error={editor.error}
                onSave={editor.save}
                onCancel={() => navigate({ to: "/w/$workspaceId/templates", params: { workspaceId } })}
            />
            {dialog}
        </SectionPageShell>
    );
}
